<template lang="pug">
.generic-wrapper
  .container
    BackButton(name="StatisticsOverview")
    h1.title Vermietungsübersicht
    .sub-headline Hier können Sie alle Vermietungseinzelheiten einsehen.

    .calendar-config.margin-top
      p <b>Schritt 1:</b> Datum auswählen
      .spacer.sm
      b-field(group)
        v-date-picker(locale="de" :columns="1", v-model="dateRange.start" :input-debounce="100")
          template(v-slot='{ inputValue, inputEvents }')
            b-field(label="Startdatum" label-position="on-border")
              input.input(:value="inputValue" v-on="inputEvents")
        .spacer.sm.inline
        v-date-picker(locale="de" :columns="1", v-model="dateRange.end" :input-debounce="100")
          template(v-slot='{ inputValue, inputEvents }')
            b-field(label="Enddatum" label-position="on-border")
              input.input(:value="inputValue" v-on="inputEvents")

    .calendar-config.margin-top
      p <b>Schritt 2:</b> Eigentümer auswählen
      .spacer.sm
      b-field(group)
        b-dropdown(
          v-model="ownerFilter"
          scrollable
          :max-height="200"
          multiple
          aria-role="list")
          template(#trigger)
            b-button(type="is-flat" icon-right="menu-down") {{ ownerFilter.length }} von {{ owners.length }} ausgewählt
          b-dropdown-item(v-for="owner in owners" :value="owner.id") {{ owner.fullName }}

    .calendar-config.margin-top
      p <b>Schritt 3:</b> Auswahl der Ferienobjekte
      .spacer.sm
      b-field(group)
        b-dropdown(
          v-model="rentObjectIdsFilter"
          scrollable
          :max-height="200"
          multiple
          aria-role="list")
          template(#trigger)
            b-button(type="is-flat" icon-right="menu-down") {{ rentObjectIdsFilter.length }} von {{ rentObjectIds.length }} ausgewählt
          b-dropdown-item(v-for="rentObject in rentObjectIds" :value="rentObject.id") {{ rentObject.code }}: {{ rentObject.name }}

    .calendar-config.margin-top
      p <b>Schritt 4:</b> Buchungselemente auswählen
      .spacer.sm
      b-field(group)
        b-dropdown(
          v-model="uniqueNamesFilter"
          scrollable
          :max-height="200"
          multiple
          aria-role="list")
          template(#trigger)
            b-button(type="is-flat" icon-right="menu-down") {{ uniqueNamesFilter.length }} von {{ uniqueNames.length }} ausgewählt
          b-dropdown-item(v-for="item in uniqueNames" :value="item") {{ item }}

      template(v-if="ownerFilter.length > 0")
        .spacer.md
        b-button(type="is-primary" icon-left="file-excel" @click="createExcel()") Excel herunterladen

  .card.margin-top
    .main
      b-table(:data="bookingItemsProcessed.table"
        default-sort-direction="asc"
        default-sort="name")
        b-table-column(field="name" label='Objekt' sortable v-slot="props")
          | {{ props.row.name }}
        b-table-column(field="parentName" label='Hauptobj.' sortable v-slot="props")
          | {{ props.row.parentName }}
        b-table-column(field="ownerName" label='Eigent.' sortable v-slot="props")
          | {{ props.row.ownerName }}
        b-table-column(field="totalIn" label='Miete erwartet' v-slot="props" sortable numeric)
          | {{ toCurrency(props.row.totalIn / 100) }}
        b-table-column(field="totalInLeft" label='Miete offen' v-slot="props" sortable numeric)
          | {{ toCurrency(props.row.totalInLeft / 100) }}
        b-table-column(field="totalCommission" label='Provision Agentur' v-slot="props" sortable numeric)
          | {{ toCurrency(props.row.totalCommission / 100) }}
        b-table-column(field="totalOut" label='Mietausgang' v-slot="props" sortable numeric)
          | {{ toCurrency(props.row.totalOut / 100) }}
        b-table-column(v-for="(itemSum, idx) in uniqueNames.filter(u => uniqueNamesFilter.find(un => un === u))" :key="'item' + idx + itemSum" :field="'item' + idx" :label="itemSum" v-slot="props" sortable numeric)
          | {{ toCurrency(props.row['item' + idx] / 100) }}
        template(#footer)
          th
          th
          th
            .th-wrap Summe
          th
            .th-wrap {{ bookingItemsProcessed.totalIn / 100 | toCurrency }}
          th
            .th-wrap {{ bookingItemsProcessed.totalInLeft / 100 | toCurrency }}
          th
            .th-wrap {{ bookingItemsProcessed.totalCommission / 100 | toCurrency }}
          th
            .th-wrap {{ bookingItemsProcessed.totalOut / 100 | toCurrency }}
          th(v-for="(itemSum, idx) in uniqueNames.filter(u => uniqueNamesFilter.find(un => un === u))" :key="'total-item' + idx")
            .th-wrap {{ toCurrency(bookingItemsProcessed['item' + idx] / 100) }}
          
          
</template>

<script>
import { DateTime } from 'luxon';
import * as ExcelJS from 'exceljs';

export default {
  data() {
    return {
      rootYear: new Date().getFullYear() - 10,
      currentYear: new Date().getFullYear(),
      year: new Date().getFullYear(),
      currentMonth: new Date().getMonth(),
      month: -1,
      ownerFilter: [],
      uniqueNames: [],
      uniqueNamesFilter: [],
      rentObjectIds: [],
      rentObjectIdsFilter: [],
      summary: {
        arrivalCount: 0,
        bookingItems: [],
        payments: [],
      },
      bookingItemsProcessed: [],
      dateRange: {
        start: new Date(new Date().getFullYear(), 0, 1, 0, 1),
        end: new Date(new Date().getFullYear(), 11, 31, 23, 59)
      },
      dateOptions: {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
      }
    }
  },
  watch: {
    dateRange: {
      async handler() {
        const loadingComponent = this.$buefy.loading.open();
        this.getNewData();
        loadingComponent.close();
      },
      deep: true
    },
    async ownerFilter(newVal) {
      const loadingComponent = this.$buefy.loading.open();
      this.rentObjectIds = this.owners.filter(o => newVal.includes(o.id)).map(o => o.rentObjects).reduce(
        function (accumulator, currentValue) {
          return accumulator.concat(currentValue);
        },
        []
      );
      this.rentObjectIdsFilter = this.rentObjectIds.map(n => n.id);
      loadingComponent.close();
    },
    uniqueNames(newVal) {
      this.uniqueNamesFilter = newVal;
    },
    uniqueNamesFilter() {
      this.bookingItemsProcessed = this.processItems(this.summary);
    },
    rentObjectIdsFilter() {
      this.getNewData();
    }
  },
  computed: {
    owners() {
      return this.$store.state.owners.all;
    },
  },
  methods: {
    async getNewData() {
      if (this.ownerFilter.length <= 0) return;
      this.summary = await this.$store.dispatch("rentObjectBookings/findBookingItems", { ...this.dateRange, filter: this.ownerFilter });
      this.getUniqueNames()
      this.bookingItemsProcessed = this.processItems(this.summary);
    },
    getUniqueNames() {
      this.uniqueNames = this.summary.bookingItems.reduce((total, item) => {
        if (total.toString().includes(item.name) || item.includeCommission === true) {
          return total;
        }
        return total + ',' + item.name
      }, 0)
      if (this.uniqueNames && this.uniqueNames.length > 0) {
        this.uniqueNames = this.uniqueNames.split(',');
        this.uniqueNames.shift();
      } else {
        this.uniqueNames = [];
      }
    },
    processItems({ arrivals, bookingItems, payments }) {
      const table = [];

      let uniqueNameCopy = this.uniqueNames;
      if (this.uniqueNamesFilter.length > 0) {
        uniqueNameCopy = this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u));
      }

      let uniqueRentObjects = arrivals.reduce((total, item) => {
        if (this.rentObjectIdsFilter.includes(item.rentObject.id) === false) {
          return total
        }
        return total.toString().includes(item.rentObject.name) ? total : total + ',' + item.rentObject.name
      }, 0)
      if (uniqueRentObjects && uniqueRentObjects.length > 0) {
        uniqueRentObjects = uniqueRentObjects.split(',');
        uniqueRentObjects.shift();
      } else {
        uniqueRentObjects = [];
      }

      for (let i = 0; i < uniqueRentObjects.length; i += 1) {
        const rentObjectName = uniqueRentObjects[i];
        const rentObjectBookingIds = arrivals.filter(a => a.rentObject.name === rentObjectName).map(b => b.id);
        const filteredBookingItems = bookingItems.filter(b => rentObjectBookingIds.includes(b.rentObjectBookingId));
        const filteredPayments = payments.filter(b => rentObjectBookingIds.includes(b.rentObjectBookingId));

        let totalPayments = 0;
        if (filteredPayments.length > 0) {
          totalPayments = filteredPayments.reduce((total, item) => {
              return total + item.total
          }, 0)
        }

        let totalIn = 0;
        if (filteredBookingItems.length > 0) {
          totalIn = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalPrice
            }
            return total;
          }, 0)
        }

        let totalInLeft = totalIn - totalPayments < 0 ? 0 : totalIn - totalPayments;

        let totalOut = 0;
        if (filteredBookingItems.length > 0) {
          totalOut = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalSettlement
            }
            return total;
          }, 0)
        }

        let totalCommission = 0;
        if (filteredBookingItems.length > 0) {
          totalCommission = filteredBookingItems.reduce((total, item) => {
            if (item.includeCommission) {
              return total + item.totalPrice - item.totalSettlement
            }
            return total;
          }, 0)
        }

        const uniqueCount = {};
        for (let u = 0; u < uniqueNameCopy.length; u += 1) {
          const name = uniqueNameCopy[u];
          uniqueCount['item' + u] = filteredBookingItems.reduce((total, item) => {
            return name === item.name ? total + item.totalPrice : total;
          }, 0);
        }


        table.push({
          name: rentObjectName,
          parentName: arrivals.find(a => a.rentObject.name === rentObjectName).rentObject.parent.name,
          ownerName: arrivals.find(a => a.rentObject.name === rentObjectName).rentObject.owner.fullName,
          totalIn,
          totalInLeft,
          totalOut,
          totalCommission,
          ...uniqueCount
        })
      }

      const totalInLeftSum = table.reduce((total, item) => {
        return total + item.totalInLeft
      }, 0);

      const totalInSum = table.reduce((total, item) => {
        return total + item.totalIn
      }, 0);

      const totalOutSum = table.reduce((total, item) => {
        return total + item.totalOut
      }, 0);

      const totalCommissionSum = table.reduce((total, item) => {
        return total + item.totalCommission
      }, 0);

      const uniqueCount = {};
      for (let u = 0; u < uniqueNameCopy.length; u += 1) {
        uniqueCount['item' + u] = table.reduce((total, item) => {
          return total + item['item' + u];
        }, 0);
      }

      return {
        totalIn: totalInSum,
        totalInLeft: totalInLeftSum,
        totalOut: totalOutSum,
        totalCommission: totalCommissionSum,
        ...uniqueCount,
        table
      }
    },
    formatDateUrl(d) {
      const dt = DateTime.fromJSDate(d);
      return dt.setLocale('de')
        .toFormat("dd-LL-yy")
    },
    formatDate(d) {
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yy")
    },
    formatDateTime(d) {
      if (d instanceof Date) {
        const dt = DateTime.fromJSDate(d);
        return dt.setLocale('de')
          .toFormat("dd.LL.yyyy")
      }
      const dt = DateTime.fromISO(d);
      return dt.setLocale('de')
        .toFormat("dd.LL.yyyy")
    },
    toCurrency(value) {
      if (typeof value !== "number") {
        return value;
      }
      var formatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      });
      return formatter.format(value);
    },
    async createExcel() {
      const wb = new ExcelJS.Workbook();

      let rootWs = wb.addWorksheet('Vermietungsübersicht', {
        pageSetup: {
          paperSize: 9,
          orientation: 'landscape',
          horizontalCentered: true,
          margins: {
            left: 0.2, right: 0.2,
            top: 0.2, bottom: 0.2,
            header: 0.1, footer: 0.1
          }
        }
      });
      this.generateWorksheet(rootWs, this.bookingItemsProcessed, `Vermietungsübersicht ${this.formatDateTime(this.dateRange.start)} bis ${this.formatDateTime(this.dateRange.end)}`);

      const self = this;
      wb.xlsx.writeBuffer({
        base64: true
      })
        .then(function (xls64) {
          // build anchor tag and attach file (works in chrome)
          var a = document.createElement("a");
          var data = new Blob([xls64], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

          var url = URL.createObjectURL(data);
          a.href = url;
          a.download = `Vermietungsübersicht_${self.formatDateUrl(self.dateRange.start)}_bis_${self.formatDateUrl(self.dateRange.end)}.xlsx`;
          document.body.appendChild(a);
          a.click();
          setTimeout(function () {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          },
            0);
        })
        .catch(function (error) {
          console.log(error.message);
        });
    },
    generateWorksheet(ws, values, name) {
      const fontOptions = {
        name: 'Arial',
        size: 9
      };
      const dynamicColumns = [];
      this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u)).forEach((uniqueName, idx) => {
        dynamicColumns.push({ header: uniqueName, key: 'item' + idx, width: 14, style: { font: fontOptions } });
      });
      const columns = [
        { header: 'Pos.', key: 'nr', width: 4, style: { font: fontOptions } },
        { header: 'Name', key: 'name', width: 22, style: { font: fontOptions } },
        { header: 'Haupt', key: 'parent', width: 22, style: { font: fontOptions } },
        { header: 'Eigent', key: 'owner', width: 22, style: { font: fontOptions } },
        { header: 'Miete erwartet', key: 'ist', width: 14, style: { font: fontOptions } },
        { header: 'Miete offen', key: 'open', width: 14, style: { font: fontOptions } },
        { header: 'Provision Agentur', key: 'soll', width: 14, style: { font: fontOptions } },
        { header: 'Mietausgang', key: 'gap', width: 14, style: { font: fontOptions } },
        { header: '', key: 'empty', width: 3, style: { font: fontOptions } },
        ...dynamicColumns
      ];
      const border = {
        top: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
      };
      ws.columns = columns;

      const rowOffset = 3;
      ws.duplicateRow(1, 2, true);
      ws.getRow(1).values = [name];
      ws.getRow(1).height = 25;
      ws.mergeCells('A1:F1');

      ws.getRow(2).values = [''];
      ws.getCell('F2').value = `erstellt am: ${this.formatDateTime(new Date())}`;
      ws.getCell('F2').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.pageSetup.printTitlesColumn = 'A:C';
      ws.pageSetup.printTitlesRow = '1:3';

      // Here we add the data
      for (let i = 0; i < values.table.length; i += 1) {
        const singleTableRow = values.table[i];
        const dynamicColumns = [];
        this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u)).forEach((_, idx) => {
          dynamicColumns.push(singleTableRow['item' + idx] / 100);
        })
        ws.insertRow(i + rowOffset + 1, [
          i + 1,
          singleTableRow.name,
          singleTableRow.parentName,
          singleTableRow.ownerName,
          singleTableRow.totalIn / 100,
          singleTableRow.totalInLeft / 100,
          singleTableRow.totalCommission / 100,
          singleTableRow.totalOut / 100,
          '',
          ...dynamicColumns
        ]);
      }

      // Gesamt Row
      const totalRow = [];
      this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u)).forEach((_, idx) => {
        totalRow.push(values['item' + idx] / 100);
      })
      ws.insertRow(values.table.length + rowOffset + 1, [
        '',
        '',
        '',
        '',
        values.totalIn / 100,
        values.totalInLeft / 100,
        values.totalCommission / 100,
        values.totalOut / 100,
        '',
        ...totalRow
      ]);
      ws.getRow(values.table.length + rowOffset + 1).font = { ...fontOptions, bold: true };

      // Custom Color Styles
      ws.getRow(1).font = { ...fontOptions, bold: true, color: { argb: 'FF000000' }, size: 14 };
      ws.getRow(2).font = { ...fontOptions, bold: true, color: { argb: 'FF000000' } };
      ws.getRow(3).font = { ...fontOptions, bold: true };

      // Here we align everything and calculate row height
      ws.eachRow(function (row, rowNr) {
        if (rowNr <= 2) return;
        let maxHeight = 0;
        row.eachCell(function (cell) {
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.border = border;
          let maxHeightTemp = cell.value.toString().split('\n').length * 25;
          if (maxHeight < maxHeightTemp) maxHeight = maxHeightTemp;
        });
        if (rowNr >= 4) {
          row.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
          row.height = maxHeight < 25 ? 25 : maxHeight;
        }
      });

      // Custom Alignment Styles
      ws.getColumn('ist').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('soll').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('gap').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('open').alignment = { vertical: 'middle', horizontal: 'right' };
      ws.getColumn('ist').numFmt = '#,##0.00 €';
      ws.getColumn('open').numFmt = '#,##0.00 €';
      ws.getColumn('soll').numFmt = '#,##0.00 €';
      ws.getColumn('gap').numFmt = '#,##0.00 €';
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(3).alignment = { vertical: 'middle', horizontal: 'center' };
      this.uniqueNames.filter(u => this.uniqueNamesFilter.find(un => un === u)).forEach((_, idx) => {
        ws.getColumn('item' + idx).alignment = { vertical: 'middle', horizontal: 'right' };
        ws.getColumn('item' + idx).numFmt = '#,##0.00 €';
      })

      ws.autoFilter = `A$${rowOffset}:C${rowOffset + values.length}`;
    }
  },
  async mounted() {
    await this.$store.dispatch("owners/findAll");
  },
}
</script>

<style lang="scss" scoped>
.items-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .item .item-price {
    font-size: 1.2rem;
    font-weight: 600;
  }
}
.th-wrap {
  justify-content: flex-end;
}
</style>